import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Loader2, ShieldCheck } from "lucide-react";
import { useWizard } from "@/context/WizardContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DownloadAuditReceipt — RN-facing "Download my responses" button.
 *
 * Posts the current wizard state to /api/wizard/audit-receipt which
 * synchronously generates and streams back a PDF receipt summarizing
 * EVERY answer the RN has entered so far, plus signature thumbnails,
 * DHS audit, and the acknowledged-skip log.
 *
 * Available at any point pre-submit AND post-submit (post-submit calls
 * pass the job_id so the receipt is co-located with the formal docs).
 */
export default function DownloadAuditReceipt({ jobId, label, variant = "ghost" }) {
  const { data } = useWizard();
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/wizard/audit-receipt`,
        { payload: data, job_id: jobId || "" },
        { responseType: "blob", timeout: 60000 },
      );
      // Trigger browser download
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Audit_Receipt_${(data?.client?.last_name || "draft")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Audit Receipt downloaded — keep this for your records.");
    } catch (e) {
      toast.error(`Download failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const cls = variant === "primary" ? "btn-primary" : "btn-ghost";
  return (
    <button
      type="button"
      onClick={download}
      disabled={busy}
      className={cls}
      data-testid="download-audit-receipt"
      title="Download a PDF of every answer you've entered, signatures, DHS audit, and acknowledged-skip log"
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (variant === "primary" ? <ShieldCheck className="w-4 h-4" /> : <Download className="w-4 h-4" />)}
      {label || "Download my responses"}
    </button>
  );
}
